import { Link } from "react-router-dom";
import type { AlertRule, Settings } from "../api/client";
import { t } from "../i18n/en";
import { usePush } from "../push/usePush";

type Step = { label: string; done: boolean; action?: React.ReactNode; blocker?: string };

/**
 * What stands between this account and an alert that actually arrives.
 *
 * On an iPhone that is four steps in a fixed order across three screens — install to the Home
 * Screen, allow notifications, give alerts somewhere to watch, create one — and nothing used to say
 * which were missing. Computed fresh every render rather than dismissed and remembered, so it can
 * never claim a step is done that has since been undone; hidden while anything is still loading, so
 * it can't flash a wrong step; and gone entirely once everything is in place.
 */
export default function SetupChecklist({ rules, settings }: { rules: AlertRule[]; settings: Settings }) {
  const push = usePush();
  if (push.status === "checking") return null;

  const steps: Step[] = [];

  if (push.status === "needs-install") {
    steps.push({ label: t.setup.install, done: false, action: <span className="hint">{t.settings.iosSteps.join(" → ")}</span> });
  }

  const pushBlocked =
    push.status === "denied" ? t.settings.pushDenied : push.status === "unconfigured" ? t.settings.pushUnconfigured : push.status === "unsupported" ? t.settings.pushUnsupported : undefined;
  steps.push({
    label: t.setup.notifications,
    done: push.status === "subscribed",
    blocker: push.status === "needs-install" ? undefined : pushBlocked,
    action:
      push.status === "prompt" ? (
        // Straight from the click: iOS only grants permission inside a user gesture.
        <button className="compact" onClick={() => void push.enable()} disabled={push.busy}>
          {t.settings.enableNotifications}
        </button>
      ) : undefined,
  });

  // Done with a home point, or when no rule is left without somewhere to point — live-only is a
  // legitimate choice (PLAN D4), so this must not nag someone who made it.
  const anchorDone = settings.home_lat !== null || (rules.length > 0 && rules.every((r) => !r.dormant_reason || !r.enabled));
  steps.push({
    label: t.setup.home,
    done: anchorDone,
    action: anchorDone ? undefined : (
      <Link className="compact-link" to="/settings">
        {t.setup.homeAction}
      </Link>
    ),
  });

  steps.push({ label: t.setup.rule, done: rules.some((r) => r.enabled) });

  if (steps.every((s) => s.done)) return null;

  return (
    <section className="panel setup">
      <h2>{t.setup.title}</h2>
      <ol className="setup-steps">
        {steps.map((step) => (
          <li key={step.label} className={step.done ? "done" : ""}>
            <span className="setup-mark" aria-hidden>
              {step.done ? "✓" : "○"}
            </span>
            <div>
              <div>{step.label}</div>
              {!step.done && step.blocker && <div className="warn">{step.blocker}</div>}
              {!step.done && !step.blocker && step.action}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
